import { snapchatAttribution, snapchatFirstGeneration } from '@/app/apis';
import { SITE } from '@/app/constants';
import { getDeviceId, safeStorage } from '@/app/utils/client';

type PixelArgs = ['init', string] | ['track', 'PAGE_VIEW'];
type SnapPixel = ((...args: PixelArgs) => void) & {
  queue: PixelArgs[];
  handleRequest?: (...args: PixelArgs) => void;
};

declare global {
  interface Window {
    snaptr?: SnapPixel;
  }
}

export const SNAPCHAT_PIXEL_ID = process.env.NEXT_PUBLIC_SNAPCHAT_PIXEL_ID ?? '';
export const SNAPCHAT_ENABLED =
  SITE === 'global' && process.env.NEXT_PUBLIC_SNAPCHAT_ENABLED === 'true' && SNAPCHAT_PIXEL_ID !== '';

let initialized = false;
const pending: PixelArgs[] = [];
const generationClicks = new Map<string, Promise<void>>();

function emit(args: PixelArgs) {
  if (initialized && window.snaptr) window.snaptr(...args);
  else pending.push(args);
}

export function initializeSnapchatPixel() {
  if (!SNAPCHAT_ENABLED || typeof window === 'undefined' || initialized) return;
  if (!window.snaptr) {
    const pixel: SnapPixel = Object.assign(
      (...args: PixelArgs) => {
        if (pixel.handleRequest) pixel.handleRequest(...args);
        else pixel.queue.push(args);
      },
      { queue: [] as PixelArgs[] },
    );
    window.snaptr = pixel;
  }
  try {
    window.snaptr('init', SNAPCHAT_PIXEL_ID);
    initialized = true;
    for (const args of pending.splice(0)) window.snaptr(...args);
  } catch {
    /* No effect on application initialization. */
  }
}

export function trackSnapchatPageView() {
  if (!SNAPCHAT_ENABLED || typeof window === 'undefined') return;
  try {
    emit(['track', 'PAGE_VIEW']);
  } catch {
    /* Optional marketing SDK. */
  }
}

// Only notify our own backend. Business conversion events are sent to Snap by CAPI.
export function trackSnapchatFirstGeneration(userId?: string): Promise<void> {
  if (!SNAPCHAT_ENABLED || !userId || typeof window === 'undefined') return Promise.resolve();
  const current = generationClicks.get(userId);
  if (current) return current;
  const task = recordGenerationClick().finally(() => generationClicks.delete(userId));
  generationClicks.set(userId, task);
  return task;
}

async function recordGenerationClick(): Promise<void> {
  try {
    await snapchatFirstGeneration();
  } catch {
    /* Optional click tracking never blocks generation; a later click may retry. */
  }
}

export function reportSnapchatAttribution() {
  if (!SNAPCHAT_ENABLED || typeof window === 'undefined') return;
  try {
    persistSnapchatAttribution();
  } catch {
    /* Browser storage or request setup must not interrupt application effects. */
  }
}

function persistSnapchatAttribution() {
  const click = new URLSearchParams(window.location.search).get('ScCid');
  const key = 'snapchat:click:v1';
  if (click && click.length <= 512) safeStorage.setItem(key, JSON.stringify({ id: click, at: Date.now() }));
  let clickId = '';
  try {
    const stored: unknown = JSON.parse(safeStorage.getItem(key) ?? '{}');
    if (
      stored &&
      typeof stored === 'object' &&
      'id' in stored &&
      'at' in stored &&
      typeof stored.id === 'string' &&
      typeof stored.at === 'number' &&
      Date.now() - stored.at < 30 * 86400000
    ) {
      clickId = stored.id;
    }
  } catch {
    /* Ignore corrupt attribution only. */
  }
  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith('_scid='));
  const sourceUrl = `${window.location.origin}${window.location.pathname}`;
  void snapchatAttribution({
    body: {
      device_id: getDeviceId(),
      sc_click_id: clickId,
      sc_cookie1: cookie?.slice('_scid='.length) ?? '',
      source_url: sourceUrl,
    },
  }).catch(() => {
    /* Retried on SDK load and authenticated user initialization. */
  });
}
